import { UNLOCK_TAKES, UNLOCK_WORDS, type SecurityModel } from "../app/security-model.js";
import { el } from "../lib/dom.js";
import { icon, type IconId } from "../lib/icon.js";
import type { AimHandlers } from "./aim.js";
import { loadFailed, setBusy } from "./busy.js";
import { tabBar, type Tab } from "./tabs.js";

/**
 * 19 Security (DCTA-111 run 2; UX spec §E.4/§E.6, mock-up 19-security.html), pushed under Me:
 * the protection status (`.shieldst`), Lock my money — the protective fast path, one sheet and no
 * passkey (the controller opens it) — Freeze a card when there is one, and the passkeys the bank
 * holds. States: loading (skeletons at the real heights, hidden from assistive tech, one status
 * line), default, locked, a lock that did not go on (loud, `role=alert`, Try again), couldn't load.
 * Locked (DCTA-232 F1): Unlock my money in Lock my money's place (`UNLOCK_WORDS`).
 * Not drawn — no read or action in the lab (backend asks): signed-in devices and sign-ins
 * (ASK-17), removing a passkey (ASK-18), the token's state.
 */
export interface SecurityHandlers {
  onBack(): void;
  onLock(): void;
  /** DCTA-232 F1: Unlock my money (`UNLOCK_WORDS`). */
  onUnlock(): void;
  /** Try again: after a lock that did not go on, or a page that did not load. */
  onRetry(): void;
  onFreeze(): void;
  onTab(tab: Tab): void;
  readonly assistant: AimHandlers;
}

export class SecurityView {
  readonly parts: readonly HTMLElement[];
  readonly topbar: HTMLElement;
  readonly body: HTMLElement;
  readonly dock: HTMLElement;
  readonly #h: SecurityHandlers;
  readonly #note: HTMLElement;
  readonly #done: HTMLElement;
  readonly #loud: HTMLElement;
  readonly #wrap: HTMLElement;

  constructor(handlers: SecurityHandlers) {
    this.#h = handlers;
    const back = el("button", { class: "raw icobtn back", type: "button", aria: { label: "Back to Me" } }, [icon("i-chev-l")]);
    back.addEventListener("click", () => this.#h.onBack());
    this.topbar = el("div", { class: "topbar" }, [back, el("span", { class: "t" }, ["Security"]), el("span", { class: "topbar-pad", aria: { hidden: "true" } })]);
    this.#note = el("span", { class: "vis-hidden loading-note", role: "status" });
    this.#done = el("div", { class: "banner done", role: "status", hidden: true });
    const retry = el("button", { class: "raw retry", type: "button" }, ["Try again"]);
    retry.addEventListener("click", () => this.#h.onRetry());
    this.#loud = el("div", { class: "banner loud", role: "alert", hidden: true }, [
      icon("i-alert"),
      el("span", { class: "bt" }, [el("b", {}, ["Your money is not locked"]), el("span", {}, ["We couldn't reach the bank. Try again, or call us and we'll lock it."])]),
      retry,
    ]);
    this.#wrap = el("div", { class: "secwrap" });
    this.body = el("div", { class: "body" }, [this.#note, this.#done, this.#loud, this.#wrap, el("div", { class: "home-end", aria: { hidden: "true" } })]);
    const bar = tabBar("me", { onTab: (t) => this.#h.onTab(t), ...this.#h.assistant }, { pushed: true });
    this.dock = bar.dock;
    this.parts = [this.topbar, this.body, this.dock];
  }

  loading(): void {
    this.#busy(true, "Loading security");
    this.#wrap.replaceChildren(el("span", { class: "sk sk-status" }), el("span", { class: "sk sk-row" }), el("span", { class: "sk sk-row" }));
  }

  show(m: SecurityModel, opts: { readonly freeze?: boolean } = {}): void {
    this.#busy(false, "");
    // Mock-up 19: locked, Unlock my money takes Lock my money's place.
    const rows: HTMLElement[] = [
      m.locked
        ? this.#row("unlockrow", "i-unlock", UNLOCK_WORDS, UNLOCK_TAKES, () => this.#h.onUnlock())
        : this.#row("lockrow", "i-lock", "Lock my money", "Stops every payment at once", () => this.#h.onLock()),
    ];
    if (opts.freeze === true) rows.push(this.#row("freezerow", "i-snow", "Freeze a card", "Stops card payments at once", () => this.#h.onFreeze()));
    // No passkey yet: a neutral mark — the green shield says "protected", which nothing is yet.
    const none = !m.locked && m.passkeys.length === 0;
    const blocks: HTMLElement[] = [
      el("div", { class: m.locked ? "shieldst locked" : none ? "shieldst none" : "shieldst" }, [
        el("span", { class: "ic", aria: { hidden: "true" } }, [icon(m.locked ? "i-lock" : none ? "i-passkey" : "i-shield")]),
        el("span", { class: "tx" }, [el("b", {}, [m.status.title]), el("small", {}, [m.status.sub])]),
      ]),
      el("div", { class: "list" }, rows),
    ];
    if (m.passkeys.length > 0) {
      blocks.push(
        el("div", { class: "sec" }, [el("h3", {}, ["Passkeys"])]),
        el(
          "div",
          { class: "list" },
          m.passkeys.map((p) =>
            el("div", { class: "setrow pk" }, [
              el("span", { class: "ic", aria: { hidden: "true" } }, [icon("i-passkey")]),
              el("span", { class: "tx" }, [el("b", {}, [p.title]), el("small", {}, [p.sub])]),
            ]),
          ),
        ),
      );
    }
    this.#wrap.replaceChildren(...blocks);
  }

  /** A protective action went on: the bank's own line, once, as a status. */
  done(line: string): void {
    this.#done.replaceChildren(icon("i-check"), el("span", { class: "bt" }, [line]));
    this.#done.hidden = false;
  }

  /** The lock did not go on (§E.2: a protective failure is loud). */
  notLocked(on: boolean): void {
    this.#loud.hidden = !on;
  }

  /** The page could not be read: say so, and what that does not touch; claim no status. */
  unreachable(): void {
    this.#busy(false, "");
    this.#wrap.replaceChildren(loadFailed("Couldn't load your security settings", "Your money and cards are not affected.", () => this.#h.onRetry(), "banner unreach"));
  }

  #busy(on: boolean, note: string): void {
    setBusy(this.body, this.#note, this.#wrap, on, note);
  }

  #row(cls: string, ic: IconId, title: string, sub: string, onSelect: () => void): HTMLElement {
    const row = el("button", { class: `raw setrow ${cls}`, type: "button" }, [
      el("span", { class: "ic", aria: { hidden: "true" } }, [icon(ic)]),
      el("span", { class: "tx" }, [el("b", {}, [title]), el("small", {}, [sub])]),
      icon("i-chev-r", "chev"),
    ]);
    row.addEventListener("click", onSelect);
    return row;
  }
}
